import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Clock,
  LogIn,
  LogOut,
  CheckCircle2,
  CalendarCheck,
  CalendarClock,
  FileClock,
  FileText,
  User,
  ArrowRight,
} from 'lucide-react';

const formatTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
};

const TODAY_CARD_CONFIG = {
  belum_absen: {
    icon: LogIn,
    color: 'bg-primary/10 text-primary',
    title: 'Belum absen hari ini',
    desc: 'Jangan lupa absen masuk ya!',
  },
  sudah_masuk: {
    icon: CheckCircle2,
    color: 'bg-green-100 text-green-600',
    title: 'Sudah absen masuk',
    desc: 'Jangan lupa absen pulang nanti',
  },
  sudah_pulang: {
    icon: LogOut,
    color: 'bg-slate-100 text-slate-500',
    title: 'Absen hari ini selesai',
    desc: 'Sampai jumpa besok!',
  },
};

const QUICK_LINKS = [
  { to: '/attendance', icon: Clock, label: 'Absen Sekarang' },
  { to: '/attendance/history', icon: CalendarCheck, label: 'Riwayat Absensi' },
  { to: '/leave', icon: CalendarClock, label: 'Ajukan Cuti' },
  { to: '/work-reports', icon: FileText, label: 'Laporan Kerja' },
  { to: '/profile', icon: User, label: 'Profil Saya' },
];

const StaffDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/dashboard/my-stats');
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch my dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const todayCfg = TODAY_CARD_CONFIG[stats?.today_status] || TODAY_CARD_CONFIG.belum_absen;
  const TodayIcon = todayCfg.icon;

  return (
    <div className="space-y-6" data-testid="staff-dashboard-page">
      {/* Greeting */}
      <div className="animate-fade-in flex items-center gap-4">
        <Avatar className="w-14 h-14">
          <AvatarImage src={stats?.foto_url} />
          <AvatarFallback className="bg-primary/10 text-primary text-lg">
            {user?.nama_lengkap?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground font-['Manrope'] tracking-tight">
            Halo, {user?.nama_lengkap?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground text-sm">
            {stats?.posisi}{stats?.site_nama ? ` \u00b7 ${stats.site_nama}` : ''}
          </p>
        </div>
      </div>

      {/* Today's Attendance */}
      <Card
        className="animate-fade-in cursor-pointer hover:shadow-md transition-shadow"
        style={{ animationDelay: '50ms' }}
        onClick={() => navigate('/attendance')}
        data-testid="today-attendance-card"
      >
        <CardContent className="p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${todayCfg.color}`}>
              <TodayIcon className="w-7 h-7" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground font-['Manrope']">{todayCfg.title}</p>
              <p className="text-sm text-muted-foreground">{todayCfg.desc}</p>
              {stats?.today_clock_in && (
                <p className="text-xs text-muted-foreground mt-1">
                  Masuk {formatTime(stats.today_clock_in)}
                  {stats?.today_clock_out && ` \u00b7 Pulang ${formatTime(stats.today_clock_out)}`}
                </p>
              )}
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        </CardContent>
      </Card>

      {/* Monthly Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="animate-fade-in" style={{ animationDelay: '100ms' }} data-testid="stat-hadir">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600 font-['Manrope']">{stats?.bulan_hadir ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Hadir bulan ini</p>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '150ms' }} data-testid="stat-terlambat">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600 font-['Manrope']">{stats?.bulan_terlambat ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Terlambat</p>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '200ms' }} data-testid="stat-izin-cuti">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary font-['Manrope']">{stats?.bulan_izin_cuti ?? 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Hari izin/cuti</p>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '250ms' }} data-testid="stat-persentase">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-foreground font-['Manrope']">{stats?.persentase_kehadiran ?? 0}%</p>
            <p className="text-xs text-muted-foreground mt-1">Kehadiran</p>
          </CardContent>
        </Card>
      </div>

      {/* Contract Reminder */}
      {stats?.tanggal_berakhir_kontrak && stats?.sisa_hari_kontrak <= 30 && (
        <Card
          className={`animate-fade-in ${stats.sisa_hari_kontrak < 0 ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}
          style={{ animationDelay: '300ms' }}
          data-testid="my-kontrak-alert"
        >
          <CardContent className="p-4 flex items-center gap-3">
            <FileClock className={`w-5 h-5 flex-shrink-0 ${stats.sisa_hari_kontrak < 0 ? 'text-red-600' : 'text-amber-600'}`} />
            <p className="text-sm font-medium">
              {stats.sisa_hari_kontrak < 0
                ? `Kontrak Anda sudah berakhir ${Math.abs(stats.sisa_hari_kontrak)} hari lalu`
                : `Kontrak Anda berakhir dalam ${stats.sisa_hari_kontrak} hari`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Quick Links */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Jalan Pintas
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {QUICK_LINKS.map((link, idx) => (
            <Card
              key={link.to}
              className="animate-fade-in cursor-pointer hover:shadow-md hover:border-primary/40 transition-all"
              style={{ animationDelay: `${350 + idx * 50}ms` }}
              onClick={() => navigate(link.to)}
              data-testid={`quick-link-${link.to.replace(/\//g, '-')}`}
            >
              <CardContent className="p-4 flex flex-col items-center gap-2 text-center">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <link.icon className="w-5 h-5 text-primary" />
                </div>
                <p className="text-sm font-medium">{link.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StaffDashboardPage;
